<template>
  <div class="upload-container bg-white border">

    <h4 class="my-5 text-center" v-if="noNetworkSyncsAllowed">{{ $t('onboarding.sync-connections.none-to-sync') }}</h4>

    <h3 class="my-3 text-center" v-else-if="canOnlySyncOneNetwork">
      <template v-if="state === 'LINKEDIN'">
        <font-awesome-icon :icon="[ 'fab', 'linkedin' ]"/><br/>
        {{ $t('onboarding.sync-connections.upload-linkedin.title') }}
      </template>
      <template v-if="state === 'GOOGLE'">
        <font-awesome-icon :icon="[ 'fab', 'google' ]"/><br/>
        {{ $t('onboarding.sync-connections.sync-google.title') }}
      </template>
      <template v-if="state === 'MICROSOFT'">
        <font-awesome-icon :icon="[ 'fab', 'windows' ]"/><br/>
        {{ $t('onboarding.sync-connections.sync-microsoft.title') }}
      </template>
    </h3>

    <b-row class="brand-selector m-0" v-else>
      <b-col cols="12" sm="4" class="p-0" v-if="preferences.canSyncContactsWithLinkedIn">
        <brand-block :class="{'active': state === 'LINKEDIN'}"
          :value="state === 'LINKEDIN'" @input="v => state = (v ? 'LINKEDIN' : null)"
          :title-text="$t('onboarding.sync-connections.upload-linkedin.title')" :last-connected="null"
          icon="linkedin-in" icon-background-colour="#0e76a8" icon-text-color="#fff"
          :button="$t('onboarding.sync-connections.upload-linkedin.cta')" button-background-colour="#0e76a8" button-text-colour="#fff"/>
      </b-col>

      <b-col cols="12" sm="4" class="p-0" v-if="preferences.canSyncContactsWithGoogle">
        <brand-block :class="{'active': state === 'GOOGLE'}"
          :value="state === 'GOOGLE'" @input="v => state = (v ? 'GOOGLE' : null)"
          :title-text="$t('onboarding.sync-connections.sync-google.title')" :last-connected="null"
          icon="google" icon-background-colour="#dc4e41" icon-text-color="#fff"
          :button="$t('onboarding.sync-connections.sync-google.cta')" button-background-colour="#dc4e41" button-text-colour="#fff">
            <template v-slot:icon>
              <b-img class="mt-3 mb-2" height="40px" width="40px" src="/static/images/google-uploads/g-logo.png"/>
            </template>
        </brand-block>
      </b-col>

      <b-col cols="12" sm="4" class="p-0" v-if="preferences.canSyncContactsWithMicrosoft">
        <brand-block :class="{'active': state === 'MICROSOFT'}"
          :value="state === 'MICROSOFT'" @input="v => state = (v ? 'MICROSOFT' : null)"
          :title-text="$t('onboarding.sync-connections.sync-microsoft.title')" :last-connected="null"
          icon="windows" icon-background-colour="#0072c6" icon-text-color="#fff"
          :button="$t('onboarding.sync-connections.sync-microsoft.cta')" button-background-colour="#0072c6" button-text-colour="#fff"/>
      </b-col>
    </b-row>

    <no-ssr v-if="state !== null">
      <component :is="openedParserComponent" v-bind="{ canOnlySyncOneNetwork, rejectedCompanyNames, rejectedDomains, redirectUri }"
        @finished-uploading="a => this.$emit('finished-uploading', a)"/>
    </no-ssr>

  </div>
</template>

<script>
import BrandBlock from '@/components/contacts-upload/BrandBlock';

export default {
  props: {
    preferences: {
      type: Object,
      default: () => ({}),
    },
    preSelect: {
      type: String,
      default: () => null,
    },
    redirectUriTemplate: {
      type: String,
      default: () => null,
    },
  },
  components: {
    BrandBlock,
  },
  data: () => ({
    state: null,
  }),
  created() {
    if (this.preSelect) {
      this.state = `${this.preSelect}`.trim().toUpperCase();
    } else if (this.canOnlySyncOneNetwork) {
      this.state = this.canOnlySyncOneNetwork;
    }
  },
  mounted() {
    if (process.env.VUE_ENV === 'client' && this.preSelect) {
      // this.$nextTick fires too quickly
      setTimeout(() => this.$scrollTo(`.contacts-upload-${this.preSelect}`), 500);
    }
  },
  computed: {
    canOnlySyncOneNetwork() {
      const canSyncNetworks = Object.keys(this.preferences)
        .filter(key => key.startsWith('canSyncContactsWith') && this.preferences[key] === true);
      return canSyncNetworks.length === 1
        ? canSyncNetworks.shift().substr('canSyncContactsWith'.length).toUpperCase()
        : null;
    },
    noNetworkSyncsAllowed() {
      return Object.keys(this.preferences)
        .filter(key => key.startsWith('canSyncContactsWith'))
        .reduce((bool, key) => bool && this.preferences[key] === false, true);
    },
    rejectedDomains() {
      return this.preferences && Array.isArray(this.preferences.ignoreContactEmailDomains)
        ? this.preferences.ignoreContactEmailDomains.map(s => s.toLowerCase())
        : null;
    },
    rejectedCompanyNames() {
      return this.preferences && Array.isArray(this.preferences.ignoreContactCompanyNames)
        ? this.preferences.ignoreContactCompanyNames.map(s => s.toLowerCase())
        : null;
    },
    openedParserComponent() {
      switch (this.state) {
        case 'LINKEDIN': return () => import('@src/components/contacts-upload/LinkedInParser');
        case 'MICROSOFT': return () => import('@src/components/contacts-upload/MicrosoftParser');
        case 'GOOGLE': return () => import('@src/components/contacts-upload/GoogleParser');
        default: return null;
      }
    },
    redirectUri() {
      if (typeof this.redirectUriTemplate === 'string' && this.redirectUriTemplate.includes('$PROVIDER')) {
        return this.redirectUriTemplate.replace('$PROVIDER', this.state.toLowerCase());
      } else {
        return null;
      }
    },
  },
};
</script>

<style lang="scss">
@import './../../scss/variables';

#linkedin-video-modal {
  .close-video-overlay {
    position: absolute;
    right: 0;
    top: 0;
    left: 0;
    bottom: 0;
    background: rgba(255,255,255, .97);
  }
}

.upload-container {
  .row.brand-selector > div {
    border-color: #dadada;
    border-left-width: 1px;
    border-left-style: solid;

    &:first-child {
      border-left-width: 0;
    }
  }

  @media (min-width: 992px) {
    .modal-lg {
      max-width: 90%;
    }
  }

  .btn {
    margin-bottom: 2em;
    position: relative;
    overflow: hidden;
    &:hover {
      opacity: 0.75;
    }
    &.linkedin {
      background-color: #0e76a8;
      border-width: 0px;
    }
    &.google {
      background-color: #dc4e41;
      border-width: 0px;
    }
    &.facebook {
      background-color: #3b5998;
      border-width: 0px;
    }
    input {
      position: absolute;
      font-size: 50px;
      opacity: 0;
      right: 0;
      top:0;
    }
  }
  .upload-modal {
    .table-caption {
      color: black;
    }
    table.b-table > thead > tr > th, table.b-table > tfoot > tr > t {
      background-color: rgba(218, 218, 218, 0.1);
      color: rgba(49,55,67, 0.5)
    }
    .finished-upload {
      img {
        margin-bottom: 2em;
      }
    }
  }

  .step-block {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: #dadada;
    padding: 1rem;
    padding-bottom: 3rem;
    padding-top: 3rem;
    width: 100%;
    font-size: $small-font-size;
    .btn {
      margin-top: 2em;
    }
    img {
      margin-bottom: 1rem;
    }
    &:first-child {
      border-right-width:: 0px;
    }
    &:last-child {
      border-left-width:: 0px;
    }
    &:nth-child(2) {
      border-left-width:: 0px;
      border-right-width:: 0px;
    }
  }

  .upload-block {
    position: relative;
    border-color: #dadada;
    border-left-width: 1px;
    border-left-style: solid;
    border-bottom-width: 1px;
    border-bottom-style: solid;

    padding: 1rem;
    width: 100%;
    // opacity: 0.5;
    &:hover {
      opacity: 1;
      .btn {
        opacity: 1;
      }
    }

    &:first-child {
      border-left-width:: 0px;
    }
    // &:last-child {
    //   border-left-width:: 0px;
    // }
    &.active {
      border-top-width: 5px;
      border-top-style: solid;
      opacity: 1;
      border-bottom-width: 0px;
    }

    .close {
      position: absolute;
      top: 10px;
      right: 10px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/contacts-upload/Container.vue